/**
 * RankingFormBuilder Component
 *
 * Visual form builder for ranking configuration.
 * Includes strategy builders with weighted attributes and tiebreakers.
 */

import type {
  RankingConfig,
  RankingStrategy,
  NormalizationMethod,
  MissingValueStrategy,
} from "@tatvex/config";
import {
  Scale,
  Plus,
  Trash2,
  GripVertical,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  Star,
} from "lucide-react";
import { useCallback, useState, useMemo } from "react";

import type {
  RankingFormBuilderProps,
  RankingWeightValue,
  RankingStrategyValue,
  FieldError,
} from "./types";
import {
  createEmptyRankingStrategy,
  createEmptyRankingWeight,
  calculateTotalWeight,
  validateWeightSum,
  generateId,
} from "./types";

// ============================================================================
// Weight Builder Sub-Component
// ============================================================================

interface WeightRowProps {
  readonly value: RankingWeightValue;
  readonly onChange: (value: RankingWeightValue) => void;
  readonly onDelete: () => void;
  readonly availableAttributes?: readonly string[];
  readonly readOnly?: boolean;
}

const NORMALIZATION_OPTIONS: readonly { value: NormalizationMethod; label: string }[] = [
  { value: "min-max", label: "Min-Max" },
  { value: "z-score", label: "Z-Score" },
  { value: "log", label: "Logarithmic" },
  { value: "percentile", label: "Percentile" },
  { value: "none", label: "None" },
];

const MISSING_VALUE_OPTIONS: readonly { value: MissingValueStrategy; label: string }[] = [
  { value: "exclude", label: "Exclude" },
  { value: "worst", label: "Worst" },
  { value: "best", label: "Best" },
  { value: "average", label: "Average" },
  { value: "zero", label: "Zero" },
];

function WeightRow({
  value,
  onChange,
  onDelete,
  availableAttributes = [],
  readOnly = false,
}: WeightRowProps): React.JSX.Element {
  const handleFieldChange = useCallback(
    <K extends keyof RankingWeightValue>(key: K, newValue: RankingWeightValue[K]) => {
      onChange({ ...value, [key]: newValue });
    },
    [value, onChange]
  );

  return (
    <div className="weight-row flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 p-3">
      {!readOnly && <GripVertical className="h-4 w-4 cursor-grab text-gray-400" />}

      {/* Attribute selector */}
      <div className="flex-1">
        {availableAttributes.length > 0 ? (
          <select
            value={value.attribute}
            onChange={(e) => handleFieldChange("attribute", e.target.value)}
            disabled={readOnly}
            className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
          >
            <option value="">Select attribute...</option>
            {availableAttributes.map((attr) => (
              <option key={attr} value={attr}>
                {attr}
              </option>
            ))}
          </select>
        ) : (
          <input
            type="text"
            value={value.attribute}
            onChange={(e) => handleFieldChange("attribute", e.target.value)}
            readOnly={readOnly}
            placeholder="Attribute key"
            className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
          />
        )}
      </div>

      {/* Weight (percentage) */}
      <div className="w-20">
        <input
          type="number"
          value={Math.round(value.weight * 100)}
          onChange={(e) =>
            handleFieldChange(
              "weight",
              Math.min(100, Math.max(0, parseInt(e.target.value, 10) || 0)) / 100
            )
          }
          readOnly={readOnly}
          min={0}
          max={100}
          className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-center text-sm"
        />
      </div>
      <span className="text-sm text-gray-500">%</span>

      {/* Direction */}
      <select
        value={value.direction}
        onChange={(e) => handleFieldChange("direction", e.target.value as "asc" | "desc")}
        disabled={readOnly}
        className="w-20 rounded-md border border-gray-300 px-2 py-1.5 text-sm"
      >
        <option value="desc">High</option>
        <option value="asc">Low</option>
      </select>

      {/* Normalization */}
      <select
        value={value.normalization}
        onChange={(e) => handleFieldChange("normalization", e.target.value as NormalizationMethod)}
        disabled={readOnly}
        className="w-28 rounded-md border border-gray-300 px-2 py-1.5 text-sm"
      >
        {NORMALIZATION_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>

      {/* Missing value strategy */}
      <select
        value={value.missingValueStrategy}
        onChange={(e) =>
          handleFieldChange("missingValueStrategy", e.target.value as MissingValueStrategy)
        }
        disabled={readOnly}
        className="w-24 rounded-md border border-gray-300 px-2 py-1.5 text-sm"
      >
        {MISSING_VALUE_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>

      {/* Delete button */}
      {!readOnly && (
        <button
          type="button"
          onClick={onDelete}
          className="rounded p-1 text-gray-400 hover:bg-gray-200 hover:text-red-500"
          aria-label="Delete weight"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

// ============================================================================
// Strategy Builder Sub-Component
// ============================================================================

interface StrategyCardProps {
  readonly value: RankingStrategyValue;
  readonly onChange: (value: RankingStrategyValue) => void;
  readonly onDelete: () => void;
  readonly availableAttributes?: readonly string[];
  readonly readOnly?: boolean;
  readonly isDefault?: boolean;
  readonly onSetDefault?: () => void;
}

function StrategyCard({
  value,
  onChange,
  onDelete,
  availableAttributes = [],
  readOnly = false,
  isDefault = false,
  onSetDefault,
}: StrategyCardProps): React.JSX.Element {
  const [isExpanded, setIsExpanded] = useState(true);

  const totalWeight = useMemo(() => calculateTotalWeight(value.weights), [value.weights]);
  const isWeightValid = useMemo(() => validateWeightSum(value.weights), [value.weights]);

  const handleFieldChange = useCallback(
    <K extends keyof RankingStrategyValue>(key: K, newValue: RankingStrategyValue[K]) => {
      onChange({ ...value, [key]: newValue });
    },
    [value, onChange]
  );

  const handleWeightChange = useCallback(
    (index: number, newWeight: RankingWeightValue) => {
      const newWeights = [...value.weights];
      newWeights[index] = newWeight;
      handleFieldChange("weights", newWeights);
    },
    [value.weights, handleFieldChange]
  );

  const handleAddWeight = useCallback(() => {
    const newWeights = [...value.weights, createEmptyRankingWeight()];
    handleFieldChange("weights", newWeights);
  }, [value.weights, handleFieldChange]);

  const handleDeleteWeight = useCallback(
    (index: number) => {
      const newWeights = value.weights.filter((_, i) => i !== index);
      handleFieldChange("weights", newWeights);
    },
    [value.weights, handleFieldChange]
  );

  const handleTiebreakerChange = useCallback(
    (text: string) => {
      const tiebreaker = text
        .split(",")
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
      handleFieldChange("tiebreaker", tiebreaker.length > 0 ? tiebreaker : undefined);
    },
    [handleFieldChange]
  );

  return (
    <div className="strategy-card rounded-lg border border-gray-200 bg-white">
      {/* Header */}
      <div
        className="flex cursor-pointer items-center justify-between border-b border-gray-100 px-4 py-3"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <Scale className="h-5 w-5 text-blue-500" />
          <div>
            <h4 className="font-medium text-gray-900">
              {value.name || "Unnamed Strategy"}
              {isDefault && (
                <span className="ml-2 inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
                  <Star className="mr-1 h-3 w-3" />
                  Default
                </span>
              )}
            </h4>
            <p className="text-sm text-gray-500">
              {value.weights.length} weight{value.weights.length !== 1 ? "s" : ""} | Total:{" "}
              {Math.round(totalWeight * 100)}%
              {!isWeightValid && (
                <span className="ml-2 text-red-500">
                  <AlertCircle className="inline h-4 w-4" /> Must equal 100%
                </span>
              )}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {!readOnly && !isDefault && onSetDefault && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onSetDefault();
              }}
              className="rounded px-2 py-1 text-sm text-gray-500 hover:bg-gray-100 hover:text-blue-600"
            >
              Set as Default
            </button>
          )}
          {!readOnly && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-red-500"
              aria-label="Delete strategy"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
          {isExpanded ? (
            <ChevronUp className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-400" />
          )}
        </div>
      </div>

      {/* Content */}
      {isExpanded && (
        <div className="space-y-4 p-4">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-gray-700">ID</label>
              <input
                type="text"
                value={value.id}
                onChange={(e) => handleFieldChange("id", e.target.value)}
                readOnly={readOnly}
                placeholder="strategy_id"
                className={`rounded-md border border-gray-300 px-3 py-2 text-sm ${
                  readOnly ? "cursor-not-allowed bg-gray-50" : ""
                }`}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-gray-700">Name</label>
              <input
                type="text"
                value={value.name}
                onChange={(e) => handleFieldChange("name", e.target.value)}
                readOnly={readOnly}
                placeholder="Strategy Name"
                className={`rounded-md border border-gray-300 px-3 py-2 text-sm ${
                  readOnly ? "cursor-not-allowed bg-gray-50" : ""
                }`}
              />
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-gray-700">Description</label>
            <input
              type="text"
              value={value.description ?? ""}
              onChange={(e) => handleFieldChange("description", e.target.value || undefined)}
              readOnly={readOnly}
              placeholder="Optional description..."
              className={`rounded-md border border-gray-300 px-3 py-2 text-sm ${
                readOnly ? "cursor-not-allowed bg-gray-50" : ""
              }`}
            />
          </div>

          {/* Weights */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700">Weights</label>
              {!readOnly && (
                <button
                  type="button"
                  onClick={handleAddWeight}
                  className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm text-blue-600 hover:bg-blue-50"
                >
                  <Plus className="h-4 w-4" />
                  Add Weight
                </button>
              )}
            </div>

            {/* Weights Header */}
            <div className="mb-2 hidden text-xs font-medium text-gray-500 md:grid md:grid-cols-[1fr,80px,80px,112px,96px,32px] md:gap-2 md:px-3">
              <span>Attribute</span>
              <span className="text-center">Weight</span>
              <span>Prefer</span>
              <span>Normalization</span>
              <span>Missing</span>
              <span></span>
            </div>

            <div className="space-y-2">
              {value.weights.map((weight, index) => (
                <WeightRow
                  key={index}
                  value={weight}
                  onChange={(w) => handleWeightChange(index, w)}
                  onDelete={() => handleDeleteWeight(index)}
                  availableAttributes={availableAttributes}
                  readOnly={readOnly}
                />
              ))}
            </div>

            {value.weights.length === 0 && (
              <p className="py-4 text-center text-sm text-gray-500">
                No weights defined. Add a weight to configure ranking.
              </p>
            )}
          </div>

          {/* Tiebreaker */}
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-gray-700">Tiebreaker Attributes</label>
            <input
              type="text"
              value={(value.tiebreaker ?? []).join(", ")}
              onChange={(e) => handleTiebreakerChange(e.target.value)}
              readOnly={readOnly}
              placeholder="attribute1, attribute2"
              className={`rounded-md border border-gray-300 px-3 py-2 text-sm ${
                readOnly ? "cursor-not-allowed bg-gray-50" : ""
              }`}
            />
            <p className="text-xs text-gray-500">
              Comma-separated list of attributes to break ties
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * RankingFormBuilder - Visual ranking configuration
 *
 * CSS Classes:
 * - ranking-form: Container element
 * - ranking-form__header: Header with title and add button
 * - ranking-form__strategies: Strategy cards container
 */
export function RankingFormBuilder({
  value,
  onChange,
  onValidate,
  readOnly = false,
  errors = [],
  className = "",
  availableAttributes = [],
}: RankingFormBuilderProps): React.JSX.Element {
  const [localErrors, setLocalErrors] = useState<readonly FieldError[]>([]);
  // Errors combined from external and local validation
  void [...errors, ...localErrors];

  const handleChange = useCallback(
    <K extends keyof RankingConfig>(key: K, newValue: RankingConfig[K]) => {
      const updated: RankingConfig = {
        ...value,
        [key]: newValue,
      };

      if (onValidate) {
        const result = onValidate(updated);
        setLocalErrors(result.errors);
      }

      onChange(updated);
    },
    [value, onChange, onValidate]
  );

  const handleStrategyChange = useCallback(
    (index: number, newStrategy: RankingStrategyValue) => {
      const newStrategies = [...value.strategies] as RankingStrategy[];
      newStrategies[index] = newStrategy as RankingStrategy;
      handleChange("strategies", newStrategies);
    },
    [value.strategies, handleChange]
  );

  const handleAddStrategy = useCallback(() => {
    const newStrategy = {
      ...createEmptyRankingStrategy(),
      id: generateId("strategy"),
    };
    const newStrategies = [...value.strategies, newStrategy] as RankingStrategy[];
    handleChange("strategies", newStrategies);
  }, [value.strategies, handleChange]);

  const handleDeleteStrategy = useCallback(
    (index: number) => {
      const newStrategies = value.strategies.filter((_, i) => i !== index);
      handleChange("strategies", newStrategies);

      // Update default if deleted strategy was default
      const deletedId = value.strategies[index]?.id;
      if (deletedId === value.defaultStrategyId && newStrategies.length > 0) {
        handleChange("defaultStrategyId", newStrategies[0]?.id ?? "");
      }
    },
    [value.strategies, value.defaultStrategyId, handleChange]
  );

  const handleSetDefault = useCallback(
    (strategyId: string) => {
      handleChange("defaultStrategyId", strategyId);
    },
    [handleChange]
  );

  return (
    <div className={`ranking-form flex flex-col gap-6 ${className}`}>
      {/* Header Info */}
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-gray-700">Vertical Key</label>
            <input
              type="text"
              value={value.verticalKey}
              onChange={(e) => handleChange("verticalKey", e.target.value)}
              readOnly={readOnly}
              placeholder="vertical-key"
              className={`rounded-md border border-gray-300 px-3 py-2 text-sm ${
                readOnly ? "cursor-not-allowed bg-gray-50" : ""
              }`}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-gray-700">Version</label>
            <input
              type="number"
              value={value.version}
              onChange={(e) => handleChange("version", parseInt(e.target.value, 10) || 1)}
              readOnly={readOnly}
              min={1}
              className={`rounded-md border border-gray-300 px-3 py-2 text-sm ${
                readOnly ? "cursor-not-allowed bg-gray-50" : ""
              }`}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-gray-700">Max Results</label>
            <input
              type="number"
              value={value.maxResults ?? 50}
              onChange={(e) =>
                handleChange("maxResults", parseInt(e.target.value, 10) || undefined)
              }
              readOnly={readOnly}
              min={1}
              max={1000}
              className={`rounded-md border border-gray-300 px-3 py-2 text-sm ${
                readOnly ? "cursor-not-allowed bg-gray-50" : ""
              }`}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-gray-700">Explain Results</label>
            <div className="flex items-center gap-2 pt-2">
              <input
                type="checkbox"
                checked={value.explainResults ?? false}
                onChange={(e) => handleChange("explainResults", e.target.checked)}
                disabled={readOnly}
                className="h-4 w-4 rounded border-gray-300"
              />
              <span className="text-sm text-gray-600">Enable</span>
            </div>
          </div>
        </div>
      </div>

      {/* Strategies Section */}
      <div>
        <div className="ranking-form__header mb-4 flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">Ranking Strategies</h3>
          {!readOnly && (
            <button
              type="button"
              onClick={handleAddStrategy}
              className="inline-flex items-center gap-1 rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              <Plus className="h-4 w-4" />
              Add Strategy
            </button>
          )}
        </div>

        <div className="ranking-form__strategies space-y-4">
          {value.strategies.map((strategy, index) => (
            <StrategyCard
              key={strategy.id || index}
              value={strategy}
              onChange={(s) => handleStrategyChange(index, s)}
              onDelete={() => handleDeleteStrategy(index)}
              availableAttributes={availableAttributes as string[]}
              readOnly={readOnly}
              isDefault={strategy.id === value.defaultStrategyId}
              onSetDefault={() => handleSetDefault(strategy.id)}
            />
          ))}
        </div>

        {value.strategies.length === 0 && (
          <div className="rounded-lg border-2 border-dashed border-gray-300 py-8 text-center">
            <Scale className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-2 text-sm text-gray-500">No ranking strategies defined.</p>
            {!readOnly && (
              <button
                type="button"
                onClick={handleAddStrategy}
                className="mt-4 inline-flex items-center gap-1 rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
              >
                <Plus className="h-4 w-4" />
                Add First Strategy
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export type { RankingFormBuilderProps };
